import { useRef } from "react";
import { composerPictureIds, filesWithinLimits, type AttachmentOut } from "@omnabase/shared";
import type { PastedPicture } from "@omnabase/ui";
import { limitRefusal, uploadFile } from "./inbox";

/** A composer's pasted pictures (the pictures-in-the-text round, 2026-10-29; brief rule 1): each is uploaded at once within the message's
 *  limits, counted with the cards beside it (20 files, 10 MB each, 25 MB together; a picture over them is refused with today's toast), and
 *  stands in the text, never among the cards. At Send the message attaches the cards and the pictures still in the text (read from its
 *  HTML), so a picture taken out of the text is not sent and removing a card never touches the text. */
export type PastedPictures = { upload: (file: File) => Promise<PastedPicture | null>; idsIn: (html: string) => string[]; reset: () => void };

export function usePastedPictures(slug: string, cards: AttachmentOut[], say: (text: string) => void): PastedPictures {
  const held = useRef<AttachmentOut[]>([]);
  const latest = useRef({ slug, cards, say }); latest.current = { slug, cards, say };
  return useRef<PastedPictures>({
    async upload(file) {
      const { slug: s, cards: c, say: tell } = latest.current;
      const { refused } = filesWithinLimits([...c, ...held.current], [file]);
      if (refused[0]) { tell(`${file.name}: ${limitRefusal(refused[0].reason)}, left out`); return null; }
      try { const a = await uploadFile(s, file); held.current.push(a); return { id: a.id, url: a.url, name: a.name }; }
      catch (e) { tell(`${file.name}: ${(e as Error).message}`); return null; }
    },
    idsIn: (html) => composerPictureIds(html).filter((id) => held.current.some((h) => h.id === id)),
    reset: () => { held.current = []; },
  }).current;
}
